import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { trackAddToCart } from "@/lib/metaPixel";

const CartContext = createContext(null);
const STORAGE_KEY = "flv_cart_v2";

const cartKey = (productId, variantId) => `${productId}::${variantId}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_e) {
      /* noop */
    }
  }, [items]);

  const addItem = (product, variant, quantity = 1) => {
    if (!variant) return;
    const key = cartKey(product.id, variant.id);
    setItems((curr) => {
      const idx = curr.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...curr];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...curr,
        {
          key,
          product_id: product.id,
          variant_id: variant.id,
          slug: product.slug,
          price: variant.price,
          product_name: product.name,
          variant_name: variant.name,
          image: product.images?.[0],
          quantity,
        },
      ];
    });
    setOpen(true);
    // Meta Pixel — AddToCart
    trackAddToCart({
      id: `${product.id}::${variant.id}`,
      name: `${product?.name?.fr || product?.name?.en || product.slug} — ${variant?.name?.fr || variant?.name?.en || variant.id}`,
      price: variant.price,
      quantity,
      currency: "EUR",
    });
  };

  const removeItem = (key) =>
    setItems((curr) => curr.filter((i) => i.key !== key));

  const updateQty = (key, quantity) =>
    setItems((curr) =>
      curr
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(0, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const { subtotal, count, shipping, total } = useMemo(() => {
    const sub = items.reduce((acc, i) => acc + i.price * i.quantity, 0);
    const cnt = items.reduce((acc, i) => acc + i.quantity, 0);
    return {
      subtotal: Number(sub.toFixed(2)),
      count: cnt,
      shipping: 0,
      total: Number(sub.toFixed(2)),
    };
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQty,
        clear,
        subtotal,
        shipping,
        total,
        count,
        open,
        setOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
